import { useSiren } from '@workspace/core-ts/api';
import { Field, FieldAdresses, FieldInput, FieldPatternInput, FieldSelectNative, TextError } from '@workspace/core-ts/components';
import { formeJuridique } from '@workspace/utils';
import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import type { FormSchemaType } from '@/schema';

export default function CompanyFields({ label }: { label: string }) {
  const methods = useFormContext<FormSchemaType>();
  const isAvenant = methods.watch('DemandeTarif.ActeGestion') === 'AV';
  const SIREN = methods.watch('DemandeTarif.ListePersonnes.0.IdSociety');
  const querySiren = useSiren(SIREN);
  // @ts-expect-error - error type from axios does not expose response property
  const isNotFound = querySiren.isError && querySiren.error.response?.status === 404;

  // @ts-expect-error - error type from axios does not expose response property
  const isUnvailable = querySiren.isError && querySiren.error.response?.status === 500;

  useEffect(() => {
    if (querySiren.isSuccess) {
      const data = querySiren.data;

      methods.setValue('DemandeTarif.ListePersonnes.0', {
        ...methods.getValues('DemandeTarif.ListePersonnes.0'),
        Nom: data.denomination,
        LegalStatus: data.statutJuridique,
        CP: data.adresse.codePostal,
        Ville: data.adresse.ville,
        Adresse1: data.adresse.rue,
        DateCreation: data.dateCreation,
      });

      void methods.trigger('DemandeTarif.ListePersonnes.0.IdSociety');
    } else if (isNotFound) {
      clearFields();
      methods.setError('DemandeTarif.ListePersonnes.0.IdSociety', {
        type: 'manual',
        message: "Le SIREN n'a pas été trouvé",
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [querySiren.isSuccess, querySiren.isError]);

  const clearFields = () => {
    methods.setValue('DemandeTarif.ListePersonnes.0.Nom', '');
    methods.setValue('DemandeTarif.ListePersonnes.0.LegalStatus', '');
    methods.setValue('DemandeTarif.ListePersonnes.0.CP', '');
    methods.setValue('DemandeTarif.ListePersonnes.0.Ville', '');
    methods.setValue('DemandeTarif.ListePersonnes.0.Adresse1', '');
    methods.setValue('DemandeTarif.ListePersonnes.0.DateCreation', '');
  };

  return (
    <>
      <Field>
        <FieldPatternInput
          label={label}
          loading={querySiren.isLoading}
          name="DemandeTarif.ListePersonnes.0.IdSociety"
          type="siren"
          validation={{
            validate: {
              isSiren: (value: string) => {
                if (value && value.toString().length !== 9) {
                  return 'Le SIREN doit contenir 9 chiffres';
                }
                if (isNotFound) {
                  return "Le SIREN n'a pas été trouvé";
                }

                if (querySiren.isLoading) {
                  return 'Recherche en cours...';
                }

                return undefined;
              },
            },
            onChange: (e) => {
              const SIREN = e.target.value;
              if (querySiren.isError || querySiren.isLoading || !SIREN || SIREN.toString().length !== 9) {
                clearFields();
              }
              methods.clearErrors();
            },
          }}
          disabled={isAvenant}
        />
        {isUnvailable ? (
          <TextError>Le service d'information sur les entreprises est momentanément indisponible. Veuillez remplir les informations manuellement.</TextError>
        ) : null}
      </Field>

      {querySiren.isSuccess ? (
        <div className="bg-card w-full text-xs sm:text-sm p-4 sm:p-5 rounded-lg">
          <dl className="grid grid-cols-2 gap-1 sm:gap-2">
            <dt>Raison sociale</dt>
            <dd>{methods.watch('DemandeTarif.ListePersonnes.0.Nom')}</dd>
            <dt>Statut juridique</dt>
            <dd>{methods.watch('DemandeTarif.ListePersonnes.0.LegalStatus')}</dd>
            <dt>Date de création</dt>
            <dd>{methods.watch('DemandeTarif.ListePersonnes.0.DateCreation')}</dd>
            <dt>Code postal</dt>
            <dd>{methods.watch('DemandeTarif.ListePersonnes.0.CP')}</dd>
            <dt>Ville</dt>
            <dd>{methods.watch('DemandeTarif.ListePersonnes.0.Ville')}</dd>
            <dt>Adresse</dt>
            <dd>{methods.watch('DemandeTarif.ListePersonnes.0.Adresse1')}</dd>
          </dl>
        </div>
      ) : null}

      {isUnvailable ? (
        <>
          <FieldInput
            label="Raison sociale"
            name="DemandeTarif.ListePersonnes.0.Nom"
          />
          <FieldSelectNative
            label="Statut juridique"
            name="DemandeTarif.ListePersonnes.0.LegalStatus"
            options={formeJuridique}
            placeholder="Choisir un statut juridique"
          />
          <FieldPatternInput
            label="Date de création"
            name="DemandeTarif.ListePersonnes.0.DateCreation"
            type="date"
          />
          <FieldAdresses
            nameCP="DemandeTarif.ListePersonnes.0.CP"
            nameVille="DemandeTarif.ListePersonnes.0.Ville"
            nameAdresse="DemandeTarif.ListePersonnes.0.Adresse1"
            nameComplement="DemandeTarif.ListePersonnes.0.Adresse2"
          />
        </>
      ) : null}
    </>
  );
}
